/* eslint-disable @stylistic/ts/indent */
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActionIcon,
    Box,
    Checkbox,
    Flex,
    Space,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { ReviewDestination, ReviewDestinationInput } from "src/graphql/generated/graphql";

import {
    ReviewDestinationType,
} from "../../hooks/reviews";
import { ConfigCustomEditor } from "./configCustomEditor";
import { ConfigEditor } from "./configEditor";

export interface ItemProps {
    isSelected: boolean;
    value: ReviewDestinationInput;
    reviewDestination: ReviewDestination;
    onToggleReviewDestination?(reviewDestination: ReviewDestination);
    onReviewDestinationNameChange?(reviewDestination: ReviewDestination, name: string);
    onReviewDestinationConfigChange?(reviewDestination: ReviewDestination, config: string);
}

export const Item = ({
    value,
    reviewDestination,
    isSelected,
    onToggleReviewDestination,
    onReviewDestinationNameChange,
    onReviewDestinationConfigChange,
}: ItemProps) => {
    const { t } = useTranslation(["common"]);

    const [displayTTLabel, setDisplayTTLabel] = useState(false);

    const isCustom = value?.type === ReviewDestinationType.custom;

    let content: ReactNode = null;
    if (isSelected) {
        switch (reviewDestination?.type) {
            case ReviewDestinationType.tt:
                break;
            case ReviewDestinationType.custom:
                content = (
                    <ConfigCustomEditor
                        config={value?.config}
                        onConfigChange={(config) =>
                            onReviewDestinationConfigChange?.(reviewDestination, config)}
                    />
                );
                break;
            default:
                content = (
                    <ConfigEditor
                        reviewDestination={reviewDestination}
                        value={value}
                        onConfigChange={(config) =>
                            onReviewDestinationConfigChange?.(reviewDestination, config)}
                    />
                );
                break;
        }
    }

    return (
        <Box mt="sm">
            <Tooltip opened={displayTTLabel} position="top-start" label="NE!">
                <Flex
                    align="center"
                    style={{
                        cursor: isCustom ?
                            undefined : "pointer",
                    }}
                    onClick={() => {
                        if (isCustom) {
                            return;
                        }

                        if (reviewDestination?.type === ReviewDestinationType.tt) {
                            setDisplayTTLabel(true);
                            setTimeout(() => setDisplayTTLabel(false), 400);
                        }

                        onToggleReviewDestination?.(reviewDestination);
                    }}
                >
                    {isCustom ? (
                        <Tooltip label={t("campaign_productreviewdestinations_remove")} withArrow>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() =>
                                    onToggleReviewDestination?.(reviewDestination)}
                            >
                                <IconTrash size="1em" />
                            </ActionIcon>
                        </Tooltip>
                    ) : (
                        <Checkbox
                            color="dark"
                            size={20}
                            checked={isSelected}
                            onChange={() => { }}
                        />
                    )}
                    {isCustom ? (
                        <TextInput
                            ml="md"
                            size="xs"
                            style={{ minWidth: "50%" }}
                            placeholder={t("campaign_productreviewdestination_name")}
                            value={value?.name}
                            onChange={(event) =>
                                onReviewDestinationNameChange?.(reviewDestination, event?.target?.value)}
                        />
                    ) : (
                        <Text ml="md" weight={500}>
                            {reviewDestination?.name}
                        </Text>
                    )}
                </Flex>
            </Tooltip>
            {content && (
                <Flex align="center">
                    <Space w="20px" />
                    <Box
                        ml="md"
                        mt="xs"
                        style={{ flex: 1 }}
                    >
                        {content}
                    </Box>
                </Flex>
            )}
        </Box>
    );
};
